import { Icon as LegacyIcon } from '@ant-design/compatible';
import React, { FC, ReactElement, useEffect, useState } from 'react';
import styled from 'styles/styled-components';
import media from 'utils/mediaStyle';

import { Viewport } from 'components/ViewportProvider';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { checkIcon } from '../Images';

import { Input as AInput, Form } from 'antd';
import Modal from 'components/Modal/Modal';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import TrackingButton from 'components/TrackingComponents/Button';
import TrackingInput from 'components/TrackingComponents/TrackingInput';
import { ContainerState } from 'containers/MainLayout/types';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { compose, Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';
import { ApplicationRootState } from 'types';
import { WriteContactUsInfoInput } from 'types/schema';
import utilsMessages from 'utils/messages';
import { EventName, sendGA4Event } from 'utils/triggerGA4Event';
import { submitContactUsShortForm } from '../actions';
import messages from '../messages';
import selectKamereoEssentialPage from '../selectors';
import { MainButton, SecondaryButton } from './CommonComponents';
import ArrowRight from '../Images/arrowRightButton.svg';

const FormItem = styled(Form.Item)`
  margin-bottom: 12px !important;
  ${media.sm`
    // width: 360px;
    `}
`;

const SubmitButton = styled(TrackingButton)`
  margin: 8px auto 0px;
  font-size: 16px;
  color: #ffffff;
  width: fit-content !important;
  background: #43a047 !important;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: none;
  border-color: #43a047 !important;
  &:hover {
    background: #6fc373 !important;
    border-color: #6fc373 !important;
  }
  &:active {
    background: #3c9041 !important;
  }
`;

const AntdInput = styled(AInput)<any>`
  border-radius: 8px !important;
`;

const PrefixIcon = styled(LegacyIcon)`
  color: rgba(0, 0, 0, 0.25);
`;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin: 0 -4px;
  ${media.sm`
    padding: 16px;
    margin: 0;
      `}
`;

const SuccessIcon = styled.img`
  width: 40px;
  height: 40px;
  margin-top: 20px;
  ${media.sm`
   width: 64px;
   height: 64px;
   margin-top: 0px;
      `}
`;

const SuccessTitle = styled.h3`
  color: #242527;
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
  ${media.sm`
  font-size: 24px;
  font-weight: 500;
  line-height: 32px;
    `}
`;

const SuccessSubText = styled.span`
  color: #242527;
  text-align: center;
  ${({ bold }) =>
    bold &&
    `
    font-weight: 500;
  `}
  font-size: 14px;
  line-height: 20px;
  ${media.sm`
  font-size: 16px;
  line-height: 24px;
    `}
`;

const SuccessSubTextPhone = styled.a`
  color: #43a047;
  text-align: center;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  display: flex;
  justify-content: center;
`;

const SeparateLine = styled.div`
  width: 100%;
  height: 1px;
  background: #eaeaeb;
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

export interface IStateProps {}

interface IDispatchProps {
  onSubmitShortForm: (data: { data: WriteContactUsInfoInput; cb: () => void }) => void;
}

interface IOwnState {
  header?: ReactElement;
  onSuccess?: () => void;
  destroyForm?: boolean;
}

type Props = IStateProps & IOwnState & IDispatchProps & RouteComponentProps;

const ShortContactForm: FC<Props> = ({ onSubmitShortForm, history, header, onSuccess, destroyForm }) => {
  const lang = localStorage.getItem('lang') || 'vi';
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [openSuccessContactModal, setOpenSuccessContactModal] = useState(false);
  const [isShortFormSuccess, setIsShortFormSuccess] = useState(false);
  const [isFormChange, setIsFormChange] = useState(false);

  useEffect(() => {
    if (isShortFormSuccess || destroyForm) {
      setLoading(false);
      setIsFormChange(false);
      form.resetFields();
    }
  }, [isShortFormSuccess, destroyForm]);

  return (
    <>
      {header}
      <Form
        form={form}
        onFinish={(values) => {
          setLoading(true);
          onSubmitShortForm({
            data: values,
            cb: () => {
              if (onSuccess) {
                onSuccess();
              }
              setOpenSuccessContactModal(true);
              setIsShortFormSuccess(true);
            },
          });
        }}
        onValuesChange={() => {
          if (!isFormChange) {
            sendGA4Event(EventName.GET_PRODUCT_CONSULTATION_FORM_FIELD_INPUT_FTB);
            setIsFormChange(true);
          }
        }}
        className="short-contact-form"
        size="large"
        layout="vertical"
      >
        <FormattedMessage {...utilsMessages.businessName}>
          {(businessName: string) => (
            <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: businessName }}>
              {(fieldRequired: string) => (
                <FormattedMessage {...utilsMessages.businessNamePlaceHolder}>
                  {(businessPlaceHolder: string) => (
                    <FormItem
                      name="businessName"
                      colon={false}
                      required={true}
                      label={businessName}
                      hasFeedback
                      rules={[
                        {
                          required: true,
                          message: fieldRequired,
                        },
                      ]}
                    >
                      <TrackingInput
                        prefix={<PrefixIcon type="shop" />}
                        placeholder={businessPlaceHolder}
                        options={{
                          action: 'Enter Company Name',
                          category: 'Contact Us Form | Input',
                        }}
                      >
                        <AntdInput />
                      </TrackingInput>
                    </FormItem>
                  )}
                </FormattedMessage>
              )}
            </FormattedMessage>
          )}
        </FormattedMessage>
        <FormattedMessage {...utilsMessages.phone}>
          {(phoneNumber: string) => (
            <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: phoneNumber }}>
              {(fieldRequired) => (
                <FormattedMessage {...utilsMessages.validatePhone}>
                  {(fieldValidate) => (
                    <FormItem
                      name="phone"
                      colon={false}
                      required={true}
                      label={phoneNumber}
                      hasFeedback
                      rules={[
                        { required: true, message: fieldRequired },
                        {
                          validator: (_, value) => {
                            if (!value || (value.length >= 10 && value.length <= 11)) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error(fieldValidate as string));
                          },
                        },
                      ]}
                      getValueFromEvent={(event) => {
                        const value = event.target.value.replace(/[^0-9]/g, '');
                        return value;
                      }}
                    >
                      <TrackingInput
                        prefix={<PrefixIcon type="phone" />}
                        placeholder={phoneNumber}
                        autoComplete="phone"
                        options={{
                          action: 'Enter Phone',
                          category: 'Contact Us Form | Input',
                        }}
                      >
                        <AntdInput />
                      </TrackingInput>
                    </FormItem>
                  )}
                </FormattedMessage>
              )}
            </FormattedMessage>
          )}
        </FormattedMessage>
        <FormattedMessage {...utilsMessages.fieldEmail}>
          {(fieldEmail) => (
            <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: 'Email' }}>
              {(fieldRequired) => (
                <FormItem
                  name="email"
                  colon={false}
                  required={true}
                  label={`Email`}
                  hasFeedback
                  rules={[
                    {
                      type: 'email',
                      message: fieldEmail,
                    },
                    { required: true, message: fieldRequired },
                    // {
                    //   validator: checkEmail(),
                    // },
                  ]}
                  normalize={(value: string) => value && value.replace(/\s/g, '')}
                >
                  <TrackingInput
                    prefix={<PrefixIcon type="mail" />}
                    placeholder="Email"
                    autoComplete="username"
                    options={{
                      action: 'Enter Email',
                      category: 'Contact Us Form | Input',
                    }}
                  >
                    <AntdInput />
                  </TrackingInput>
                </FormItem>
              )}
            </FormattedMessage>
          )}
        </FormattedMessage>
        <FormItem style={{ marginTop: '12px' }}>
          <SubmitButton
            block
            type="primary"
            htmlType="submit"
            trackingCategory="Contact Us Form | Button"
            trackingAction="Submit Contact Us"
            loading={loading}
          >
            <FormattedMessage {...messages.sendInfo} />
            <SVGIcon src={ArrowRight} />
          </SubmitButton>
        </FormItem>
      </Form>
      <Modal
        isOpen={openSuccessContactModal}
        maskClosable={true}
        closable={true}
        onClose={() => {
          setOpenSuccessContactModal(false);
          setIsShortFormSuccess(false);
        }}
      >
        <ModalContainer>
          <SuccessIcon src={checkIcon} />
          <SuccessTitle>
            <FormattedMessage {...messages.registerSuccess} />
          </SuccessTitle>
          <SuccessSubText>
            <FormattedMessage
              {...messages.subTextContact}
              values={{
                workingTime: (
                  <strong>
                    <FormattedMessage {...messages.workingTime} />
                  </strong>
                ),
              }}
            />
          </SuccessSubText>
          <SuccessSubText>
            <FormattedMessage {...messages.subTextContactBelow} />
            <Viewport.Consumer>
              {({ width }) => {
                const isNotMobile = width > 1024;
                return isNotMobile ? (
                  <SuccessSubText bold> 0812 46 37 27</SuccessSubText>
                ) : (
                  <SuccessSubTextPhone href="tel:0812463727"> 0812 46 37 27</SuccessSubTextPhone>
                );
              }}
            </Viewport.Consumer>
          </SuccessSubText>
          <SeparateLine />
          <SuccessSubText bold>
            <FormattedMessage {...messages.learnMoreUs} />
          </SuccessSubText>
          <ButtonWrapper>
            <SecondaryButton
              onClick={() =>
                (window.location.href = `https://kamereo.vn/${lang === 'vi' ? 've-kamereo' : 'about-kamereo'}`)
              }
              fullWidth
            >
              <FormattedMessage {...messages.aboutUs} />
            </SecondaryButton>
            <MainButton fullWidth onClick={() => history.push(`/`)}>
              <FormattedMessage {...messages.explorerMore} />
            </MainButton>
          </ButtonWrapper>
        </ModalContainer>
      </Modal>
    </>
  );
};

const mapStateToProps = createStructuredSelector<ApplicationRootState, IStateProps>({});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  onSubmitShortForm: (data: { data: WriteContactUsInfoInput; cb: () => void }) =>
    dispatch(submitContactUsShortForm(data)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(withRouter(ShortContactForm));
